import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { blobBindingsFileSchema, blobBucketPhysicalSchema } from "./blob-bindings.js";
import { PLATFORM_BUCKETS_MAX_ORGS } from "./platform-buckets.js";
import { BlobRelocationStateSchema, refineBlobRelocationStateSet } from "./desired-state.schemas.js";
import { BlobBucketSettingsReportSchema, BlobRelocationRequestReportSchema, blobBucketSettingsKey, refineDistinctBlobBucketSettings, refineDistinctBlobRelocationRequests } from "./status-report.schemas.js";
import { BLOB_BUCKET_NAME_MAX_LENGTH, allowedLocationsSchema, blobBucketNameSchema, dataLocationSchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/blob-bucket-sync.schemas.ts
/**
* The bodies of the four operator-token routes of the blob bucket sync between the zion-operator
* program and core-api (2026-10-13-blob-buckets R-069, R-071 items 3 to 5, R-089 items 2 and 3):
*
* - `GET /internal/v1/blob-bucket-settings`: core-api answers the org settings of every allocatable
*   bucket of every accepted adoption ({@link BlobBucketSettingsSyncResponseSchema}). The operator
*   program re-keys each row to the CP org id and pages the rows into the status report member
*   `blobBucketSettings`.
* - `PUT /internal/v1/blob-bucket-facts`: the operator program sends the data plane's allowed list,
*   the data location of each org and the buckets that exist ({@link BlobBucketFactsIngestRequestSchema}).
*   core-api writes `release.dp_status.allowed_locations`, `release.org_data_locations` and
*   `bound_at` of `release.org_blob_bucket_settings` (WS-47).
* - `GET /internal/v1/blob-relocation-requests`: core-api answers the open org admin relocation
*   requests ({@link BlobRelocationRequestsSyncResponseSchema}). The operator program re-keys each
*   row to the CP org id and pages the rows into the status report member `blobRelocationRequests`.
* - `PUT /internal/v1/blob-relocation-states`: the operator program sends the full set of the
*   desired-state member `blobRelocationStates`, re-keyed to DP org ids
*   ({@link BlobRelocationStatesIngestRequestSchema}). core-api stores the set for the org admin
*   view and closes each of its requests that a closed row names (WS-51).
*
* The four routes sit beside `GET /internal/v1/app-adoptions` and `PUT /internal/v1/app-offers` on
* `@Controller() @Public() @UseGuards(OperatorTokenGuard)` in core-api (WS-47). Those two bodies
* live in core-api's `app-adoption-sync.schemas.ts`; these live here because the operator program
* (WS-46) and core-api (WS-47) both bind them, and neither may import the other.
*
* The id space of every org id on all four routes is the data plane org id (`auth.organizations.id`).
* core-api holds no CP org id. The operator translates through the roster on slug, as for
* `appAdoptions`.
*
* A 404 from any of the four routes means a core-api that predates that route. The operator program counts it as
* "route absent": one warn line, no failure streak, a retry on the next plan change, and it never
* throws from a status report (R-071 item 5).
*
* Strict at every depth. No `.transform()`, so `createOpenApiDocument()` can represent each schema.
* Pure: no clock, no filesystem, no network.
*/
/** core-api's route of the settings read: `GET /internal/v1/blob-bucket-settings` (R-071 item 3). */
const BLOB_BUCKET_SETTINGS_SYNC_ROUTE = "/internal/v1/blob-bucket-settings";
/** core-api's route of the facts ingest: `PUT /internal/v1/blob-bucket-facts` (R-071 item 3). */
const BLOB_BUCKET_FACTS_INGEST_ROUTE = "/internal/v1/blob-bucket-facts";
/**
* The most rows one array of one blob bucket sync call may carry: 65,536. It bounds `settings` of
* one page of the settings answer and `boundBuckets` of one facts call. It is a page size, not a
* data plane cap: the data plane may hold more rows than this (10,000 orgs, each with several apps
* of up to 16 buckets). The settings read pages the rest with `next` and `after`, and the operator
* program splits the bound buckets over several facts calls.
*
* Size: one settings row is at most 318 bytes of compact JSON, so a full settings page is at most
* 20,906,145 bytes (about 20 MB) with its longest `next`. One bound bucket row is at most 246 bytes
* (a 64-character `appSlug`, a 63-character `logical` and a 31-character `location`), so a full
* facts call with 16 locations and 10,000 orgs is at most 16,917,996 bytes. core-api's own JSON
* parser on the facts route must admit that body (WS-47). An over-cap array is refused, not trimmed.
* Exported so the producers read one literal.
*/
const BLOB_BUCKET_SYNC_ROWS_MAX = 65536;
/** The longest page cursor: two UUIDs, the longest logical bucket name and two slashes. */
const BLOB_BUCKET_SETTINGS_CURSOR_MAX_LENGTH = 74 + BLOB_BUCKET_NAME_MAX_LENGTH;
/**
* One page cursor of the settings read: the {@link blobBucketSettingsKey} of one settings row,
* `<orgId>/<appId>/<logical>`, where both ids are UUIDs and `logical` is in `blobBucketNameSchema`.
* The two UUIDs have a fixed length, so the byte order of two cursors is the order of their triples.
* No `.transform()`.
*/
const blobBucketSettingsCursorSchema = z.string().max(BLOB_BUCKET_SETTINGS_CURSOR_MAX_LENGTH).refine((cursor) => {
	const parts = cursor.split("/");
	return parts.length === 3 && z.uuid().safeParse(parts[0]).success && z.uuid().safeParse(parts[1]).success && blobBucketNameSchema.safeParse(parts[2]).success;
}, { message: "a cursor <orgId>/<appId>/<logical> from the next of a settings page" });
/**
* The query of `GET /internal/v1/blob-bucket-settings`, which core-api binds with
* `@Query({ schema })` (WS-47):
*
* - `after`: absent for the first page; else the `next` of the page before. core-api answers the rows
*   whose `(orgId, appId, logical)` triple comes after it in byte order.
*
* Refusals, each a `ZodError` (core-api's strict pipe answers 400): an unknown key; an `after` that
* is not a cursor `<orgId>/<appId>/<logical>` (two UUIDs and a bucket name), at `['after']`.
* A cursor of a row that core-api no longer holds is valid: the page starts after it. No
* `.transform()`. Pure.
*/
const BlobBucketSettingsSyncQuerySchema = z.object({ after: blobBucketSettingsCursorSchema.optional() }).strict();
/**
* The 200 body of `GET /internal/v1/blob-bucket-settings`: `{ settings, next }`, one page of the
* `BlobBucketSettingsReportSchema` rows, one row per accepted adoption and declared allocatable
* bucket (`scope: org`, `locality: central`) of every org of the data plane. `orgId` is the DP org
* id here (see the file header). A bucket with no settings row answers `location: null`,
* `deleteAfterDays: null` and the stored default of `removalGraceDays` (30), so an org that accepted
* before stage 3b is never blocked (R-071 item 3).
*
* The page protocol. core-api orders every row by `(orgId, appId, logical)` in byte order and answers
* at most {@link BLOB_BUCKET_SYNC_ROWS_MAX} rows after the query's `after`. `next` is the
* {@link blobBucketSettingsKey} of the last row when more rows follow, else `null`. The operator
* program reads the pages in turn, with `after` set to each `next`, until `next` is `null`.
*
* Valid input: 0 to {@link BLOB_BUCKET_SYNC_ROWS_MAX} rows in ascending key order, each
* `(orgId, appId, logical)` once, and a `next` that is `null` or the key of the last row.
*
* Refusals, each a `ZodError`: an unknown key, a missing `settings` or a missing `next` (strict); a
* row that fails `BlobBucketSettingsReportSchema`, at `['settings', index, ...]`; a repeated triple,
* at `['settings', index, 'logical']`; a row whose key sorts before the key of the row before it, at
* `['settings', index, 'logical']`; more than 65,536 rows, at `['settings']`; a `next` that is not a
* cursor, or that is not the key of the last row, at `['next']`.
*
* Who calls it: core-api's specs and contract suite (WS-47), and the operator program's client
* (WS-46), which may parse each row alone with `BlobBucketSettingsReportSchema` to keep the valid
* siblings of a bad row. A conforming body parses to itself. Pure.
*/
const BlobBucketSettingsSyncResponseSchema = z.object({
	settings: z.array(BlobBucketSettingsReportSchema).max(BLOB_BUCKET_SYNC_ROWS_MAX).superRefine(refineDistinctBlobBucketSettings).superRefine((rows, ctx) => {
		let previous;
		rows.forEach((row, i) => {
			const key = blobBucketSettingsKey(row);
			if (previous !== void 0 && key < previous) ctx.addIssue({
				code: "custom",
				path: [i, "logical"],
				message: "rows in ascending (orgId, appId, logical) order"
			});
			previous = key;
		});
	}),
	next: blobBucketSettingsCursorSchema.nullable()
}).strict().superRefine((page, ctx) => {
	if (page.next === null) return;
	const last = page.settings.at(-1);
	if (last === void 0 || blobBucketSettingsKey(last) !== page.next) ctx.addIssue({
		code: "custom",
		path: ["next"],
		message: "next is the key of the last row of the page"
	});
});
/**
* The grammar of a physical bucket location in the bindings: the `location` of
* `blobBucketPhysicalSchema`, one field that its three provider arms share.
*/
const boundLocationSchema = blobBucketPhysicalSchema.options[0].shape.location;
/**
* One bucket that exists for one org, as the operator program sees it in the bindings it writes
* (2026-10-13-blob-buckets R-071 item 3). core-api sets `bound_at` and the bound location on the
* matching settings row.
*
* - `orgId`: the DP org id, a UUID.
* - `appSlug`: the app slug, in the grammar of `BlobBindingsFile.app` (2 to 64 characters of
*   `RESOURCE_SLUG_PATTERN`), the slug of the bindings file the operator writes.
* - `logical`: the logical bucket name, in `blobBucketNameSchema` of `@zion/sdk-contracts`.
* - `location`: the location of the physical bucket, the `location` of its binding (at most 31
*   characters of `BLOB_BUCKET_LOCATION_PATTERN`). The control plane created the bucket there, and
*   the location never changes (R-065 item 3). A later move of the org data location does not move
*   it, so core-api reads the place of an existing bucket from this member alone.
*
* Strict. A conforming object parses to itself. Pure.
*/
const BlobBucketBoundBucketSchema = z.object({
	orgId: z.uuid(),
	appSlug: blobBindingsFileSchema.shape.app,
	logical: blobBucketNameSchema,
	location: boundLocationSchema
}).strict();
/**
* The body of `PUT /internal/v1/blob-bucket-facts` (R-071 item 3): the facts of the data plane that
* core-api cannot read itself.
*
* - `allowedLocations`: the data plane's allowed list, `allowedLocationsSchema` of
*   `@zion/sdk-contracts` (1 to 16 distinct single regions). The operator program copies
*   `DesiredStateEnvelope.orgDataLocations.allowedLocations`.
* - `orgDataLocations`: DP org id (a UUID) to that org's resolved data location
*   (`dataLocationSchema`), at most `PLATFORM_BUCKETS_MAX_ORGS` (10,000) keys. The operator program
*   re-keys `orgDataLocations.byOrg` of the envelope and leaves out an org with no pairing. Every
*   value is an entry of `allowedLocations`. core-api replaces the set of
*   `release.org_data_locations` with it.
* - `boundBuckets`: buckets of the data plane that hold a ready binding, at most
*   {@link BLOB_BUCKET_SYNC_ROWS_MAX} rows per call, each `(orgId, appSlug, logical)` once
*   ({@link BlobBucketBoundBucketSchema}). core-api sets `bound_at` and the bound location on each
*   named row and changes no row that the call does not name. So the operator program splits more
*   bound buckets than the cap over several calls, each with the full `allowedLocations` and
*   `orgDataLocations`, as it may split the map of `PUT /internal/v1/app-offers`.
*
* A bound location that differs from the stored pick of its row means the pick reached the control
* plane after the control plane created the bucket (R-069 item 5). core-api then replaces the stored
* pick with the bound location, so the org admin's row names the place where the data lives.
*
* Refusals, each a `ZodError` (core-api's global strict pipe answers 400 for each, before the
* handler runs):
* - an unknown key at any depth, or a missing member;
* - an `allowedLocations` that fails its schema;
* - an org key that is not a UUID, a value outside the grammar, or more than 10,000 keys (message
*   `at most 10000 orgs`, path `['orgDataLocations']`);
* - an org location that is not an entry of `allowedLocations`, at `['orgDataLocations', key]`;
* - a bound bucket row that fails {@link BlobBucketBoundBucketSchema}, more than 65,536 rows, or a
*   repeated triple, at `['boundBuckets', index, 'logical']`.
*
* An org id that core-api does not know passes this schema; core-api refuses it per row (WS-47).
*
* Who calls it: the operator program's client before it sends (WS-46), and core-api's route binding
* (WS-47). A conforming body parses to itself. Pure.
*/
const BlobBucketFactsIngestRequestSchema = z.object({
	allowedLocations: allowedLocationsSchema,
	orgDataLocations: z.record(z.uuid(), dataLocationSchema).refine((byOrg) => Object.keys(byOrg).length <= PLATFORM_BUCKETS_MAX_ORGS, { message: `at most ${PLATFORM_BUCKETS_MAX_ORGS} orgs` }),
	boundBuckets: z.array(BlobBucketBoundBucketSchema).max(BLOB_BUCKET_SYNC_ROWS_MAX).superRefine((rows, ctx) => {
		const seen = /* @__PURE__ */ new Set();
		rows.forEach((row, i) => {
			const key = `${row.orgId}/${row.appSlug}/${row.logical}`;
			if (seen.has(key)) ctx.addIssue({
				code: "custom",
				path: [i, "logical"],
				message: "duplicate (orgId, appSlug, logical) triple"
			});
			seen.add(key);
		});
	})
}).strict().superRefine((body, ctx) => {
	const allowed = new Set(body.allowedLocations);
	for (const [orgId, location] of Object.entries(body.orgDataLocations)) if (!allowed.has(location)) ctx.addIssue({
		code: "custom",
		path: ["orgDataLocations", orgId],
		message: "an org data location is an entry of allowedLocations"
	});
});
/** core-api's route of the open relocation requests: `GET /internal/v1/blob-relocation-requests`. */
const BLOB_RELOCATION_REQUESTS_SYNC_ROUTE = "/internal/v1/blob-relocation-requests";
/** core-api's route of the relocation states: `PUT /internal/v1/blob-relocation-states`. */
const BLOB_RELOCATION_STATES_INGEST_ROUTE = "/internal/v1/blob-relocation-states";
/** A lower-case UUID: a request id, a page cursor or a DP org id. */
const lowerUuidSchema = z.string().regex(LOWERCASED_UUID_PATTERN, "a lower-case UUID");
/**
* The query of `GET /internal/v1/blob-relocation-requests`, which core-api binds with
* `@Query({ schema })` (WS-51):
*
* - `after`: absent for the first page; else the `next` of the page before, a lower-case UUID.
*   core-api answers the requests whose `requestId` comes after it in byte order.
*
* Refusals, each a `ZodError` (core-api's strict pipe answers 400): an unknown key; an `after` that
* is not a lower-case UUID, at `['after']`. A cursor of a request that core-api no longer holds open
* is valid: the page starts after it. No `.transform()`. Pure.
*/
const BlobRelocationRequestsSyncQuerySchema = z.object({ after: lowerUuidSchema.optional() }).strict();
/**
* The 200 body of `GET /internal/v1/blob-relocation-requests`: `{ requests, next }`, one page of the
* open org admin relocation requests of every org of the data plane, as
* `BlobRelocationRequestReportSchema` rows. `orgId` is the DP org id here (see the file header).
*
* An open request is a row of `release.org_blob_relocation_requests` that core-api has not closed.
* core-api closes a request when a `PUT /internal/v1/blob-relocation-states` body names its
* `requestId` in a closed state (`BLOB_RELOCATION_CLOSED_STATES` of `relocation.ts`). core-api also
* closes an open request when the org admin sends a new request of the same app and the open one is
* unanswered: no stored state row names its `requestId`, and its `requested_at` is more than 7 days
* old (`BLOB_RELOCATION_REQUEST_UNANSWERED_DAYS` of core-api, R-092 item 1). That close writes
* `closed_state` `refused` and `closed_refusal` `relocation_request_unanswered`. So a request
* stays on these pages while its plan runs, and the operator program's ledger keeps it from a second
* report until a restart.
*
* The page protocol. core-api orders the open requests by `requestId` in byte order and answers at
* most {@link BLOB_BUCKET_SYNC_ROWS_MAX} rows after the query's `after`. `next` is the `requestId` of
* the last row when more rows follow, else `null`. The operator program reads the pages in turn, with
* `after` set to each `next`, until `next` is `null`.
*
* Refusals, each a `ZodError`: an unknown key, a missing `requests` or a missing `next` (strict); a
* row that fails `BlobRelocationRequestReportSchema`, at `['requests', index, ...]`; a repeated
* `requestId`, at `['requests', index, 'requestId']`; a row whose `requestId` sorts before the one of
* the row before it, at `['requests', index, 'requestId']`; more than 65,536 rows, at
* `['requests']`; a `next` that is not a lower-case UUID, or that is not the `requestId` of the last
* row, at `['next']`.
*
* Who calls it: core-api's specs and contract suite (WS-51), and the operator program's client
* (WS-20), which may parse each row alone with `BlobRelocationRequestReportSchema` to keep the valid
* siblings of a bad row. A conforming body parses to itself. Pure.
*/
const BlobRelocationRequestsSyncResponseSchema = z.object({
	requests: z.array(BlobRelocationRequestReportSchema).max(BLOB_BUCKET_SYNC_ROWS_MAX).superRefine(refineDistinctBlobRelocationRequests).superRefine((rows, ctx) => {
		let previous;
		rows.forEach((row, i) => {
			if (previous !== void 0 && row.requestId < previous) ctx.addIssue({
				code: "custom",
				path: [i, "requestId"],
				message: "rows in ascending requestId order"
			});
			previous = row.requestId;
		});
	}),
	next: lowerUuidSchema.nullable()
}).strict().superRefine((page, ctx) => {
	if (page.next === null) return;
	if (page.requests.at(-1)?.requestId !== page.next) ctx.addIssue({
		code: "custom",
		path: ["next"],
		message: "next is the requestId of the last row of the page"
	});
});
/**
* The body of `PUT /internal/v1/blob-relocation-states` (R-089 item 3): `{ states }`, the full set of
* the desired-state member `blobRelocationStates` that the operator program holds, re-keyed to DP
* org ids, 0 to `BLOB_RELOCATION_STATES_MAX` (256) `BlobRelocationStateSchema` rows under the set
* rules of `refineBlobRelocationStateSet` (ascending key order, each request id and relocation id
* once, one open row per `(orgId, appSlug)`). `{ states: [] }` is valid: the CP holds no row.
*
* core-api's write (WS-51), in one transaction per org under RLS:
* 1. It replaces the stored state set of the data plane with the rows of the body. A row of an org
*    that core-api does not know is not stored, and its org id is in `unknownOrgIds` of the answer.
* 2. For each stored row with a `requestId` and a closed state, it closes the open request of that
*    id with the row's state and refusal. A closed request stays closed. A `requestId` that core-api
*    does not hold closes nothing.
*
* Refusals, each a `ZodError` (core-api's global strict pipe answers 400 before the handler runs):
* an unknown key at any depth, or a missing `states`; a row that fails `BlobRelocationStateSchema`,
* at `['states', index, ...]`; more than 256 rows, at `['states']`; a broken set rule, at the paths
* of `refineBlobRelocationStateSet` under `['states']`.
*
* Who calls it: the operator program's client before it sends (WS-20), and core-api's route binding
* (WS-51). A conforming body parses to itself. Pure.
*/
const BlobRelocationStatesIngestRequestSchema = z.object({ states: z.array(BlobRelocationStateSchema).max(256).superRefine(refineBlobRelocationStateSet) }).strict();
/**
* The 200 body of `PUT /internal/v1/blob-relocation-states`: `{ stored, unknownOrgIds }`.
*
* - `stored`: the count of rows core-api stored, an integer from 0 to 256.
* - `unknownOrgIds`: the distinct org ids of the rows that core-api did not store because it holds
*   no such org, lower-case UUIDs in ascending byte order, at most 256.
*
* Refusals, each a `ZodError`: an unknown key or a missing member (strict); a `stored` outside 0 to
* 256 or not an integer, at `['stored']`; an org id outside the grammar, at
* `['unknownOrgIds', index]`; an org id that does not sort after the one before it, at
* `['unknownOrgIds', index]`; more than 256 ids, at `['unknownOrgIds']`.
*
* Who calls it: core-api's specs (WS-51), and the operator program's client (WS-20), which logs the
* unknown org ids and counts the call a success. A conforming body parses to itself. Pure.
*/
const BlobRelocationStatesIngestResultSchema = z.object({
	stored: z.int().min(0).max(256),
	unknownOrgIds: z.array(lowerUuidSchema).max(256).superRefine((ids, ctx) => {
		ids.forEach((id, i) => {
			const previous = ids[i - 1];
			if (previous !== void 0 && id <= previous) ctx.addIssue({
				code: "custom",
				path: [i],
				message: "org ids in ascending order, each once"
			});
		});
	})
}).strict();
//#endregion
export { BLOB_BUCKET_FACTS_INGEST_ROUTE, BLOB_BUCKET_SETTINGS_SYNC_ROUTE, BLOB_BUCKET_SYNC_ROWS_MAX, BLOB_RELOCATION_REQUESTS_SYNC_ROUTE, BLOB_RELOCATION_STATES_INGEST_ROUTE, BlobBucketBoundBucketSchema, BlobBucketFactsIngestRequestSchema, BlobBucketSettingsSyncQuerySchema, BlobBucketSettingsSyncResponseSchema, BlobRelocationRequestsSyncQuerySchema, BlobRelocationRequestsSyncResponseSchema, BlobRelocationStatesIngestRequestSchema, BlobRelocationStatesIngestResultSchema };

//# sourceMappingURL=blob-bucket-sync.schemas.js.map